import { useState, useRef, useEffect, useCallback } from "react";
import { useProducts } from "@/contexts/ProductContext";
import { useCustomers } from "@/contexts/CustomerContext";
import { apiRequest } from "@/lib/api";
import {
  Wand2, Upload, Image, Loader2, ShoppingCart, Sofa, Lightbulb, Info, Eye,
  Layers, ArrowRight, Download, Pin, Check, X, User, Sparkles, Camera,
  MessageSquare, Send, Move, ZoomIn, RotateCw, RefreshCw, Replace, Plus,
  Monitor, CornerDownLeft, CornerDownRight, Maximize, ChevronRight, Palette, ArrowLeft,
  Armchair, Lamp, Tv, BedDouble, Table2, Coffee, Square
} from "lucide-react";
import "./RoomRebuild.css";

interface PlacementResult {
  furnitureType: string;
  furnitureName: string;
  roomType: string;
  roomAnalysis?: {
    existingFurniture?: string[];
    replacingItem?: string;
    openSpaces?: string[];
    perspectiveAngle?: string;
    floorLevel?: number;
  };
  placement: { x: number; y: number; scale: number; rotation: number };
  alternativePlacements?: { label: string; x: number; y: number; scale: number; rotation: number }[];
  stagingDescription: string;
  placementAdvice: string;
  styleMatch: string;
  shadowDirection?: string;
  depthScale?: string;
  complementaryItems?: string[];
}

interface RebuildResult {
  analysis: {
    roomAnalysis?: {
      roomType?: string;
      currentStyle?: string;
      dimensions?: string;
      lighting?: string;
      colorPalette?: string[];
      wallColor?: string;
      floorType?: string;
    };
    existingFurniture?: string[];
    keepItems?: string[];
    replaceItems?: string[];
    recommendedProducts?: { name: string; collection?: string; category?: string; placement: string; reason?: string; replaces?: string; searchSlug?: string; placementZone?: { x: number; y: number; width: number; height: number } }[];
    designNotes?: string;
    redesignDescription?: string;
    salesTips?: string[];
  };
  originalImageUrl: string | null;
}

interface VisualizeResult {
  images: { url: string; style: string; furniture: string; room: string; colors: string[] }[];
  description: string;
  prompt: string;
  analysis?: RebuildResult["analysis"];
  roomPhotoUrl?: string;
}

const FURNITURE_CATEGORIES = [
  "Sofa", "Sectional", "Recliner", "Loveseat", "Accent Chair",
  "Coffee Table", "End Table", "TV Stand", "Dining Table", "Dining Chair",
  "Bed", "Dresser", "Nightstand", "Desk", "Bookshelf"
];

const PLACEMENT_PRESETS = [
  { id: "center-wall", label: "Center Wall", icon: Monitor },
  { id: "left-corner", label: "Left Corner", icon: CornerDownLeft },
  { id: "right-corner", label: "Right Corner", icon: CornerDownRight },
  { id: "open-center", label: "Open Center", icon: Maximize },
  { id: "facing-camera", label: "Facing Camera", icon: Eye },
  { id: "window-wall", label: "Window Wall", icon: Layers },
];

const STYLE_OPTIONS = ["Modern", "Traditional", "Rustic", "Mid-Century", "Contemporary", "Farmhouse", "Industrial", "Coastal", "Transitional"];
const FURNITURE_OPTIONS = ["Sofa", "Sectional", "Recliner", "Loveseat", "Dining Set", "Bedroom Set", "Accent Chair"];
const COLOR_OPTIONS = ["Navy", "Gray", "Cream", "Brown", "White", "Teal", "Burgundy", "Sage", "Gold", "Charcoal"];

export default function RoomRebuild() {
  const { products } = useProducts();
  const { customers, updateCustomer } = useCustomers();
  const [pageMode, setPageMode] = useState<"rebuild" | "airebuild" | "visualize">("airebuild");

  const [step, setStep] = useState(1);
  const [roomPhoto, setRoomPhoto] = useState<string | null>(null);
  const [roomPhotoBase64, setRoomPhotoBase64] = useState<string | null>(null);
  const [productPhoto, setProductPhoto] = useState<string | null>(null);
  const [productPhotoBase64, setProductPhotoBase64] = useState<string | null>(null);
  const [productCutout, setProductCutout] = useState<string | null>(null);
  const [isRemovingBg, setIsRemovingBg] = useState(false);
  const [mode, setMode] = useState<"replace" | "add">("replace");
  const [furnitureCategory, setFurnitureCategory] = useState("");
  const [placementPreset, setPlacementPreset] = useState("center-wall");
  const [isGenerating, setIsGenerating] = useState(false);
  const [generateStep, setGenerateStep] = useState(0);
  const [placementResult, setPlacementResult] = useState<PlacementResult | null>(null);
  const [compositeUrl, setCompositeUrl] = useState<string | null>(null);
  const [error, setError] = useState("");

  const [furnitureX, setFurnitureX] = useState(0.5);
  const [furnitureY, setFurnitureY] = useState(0.7);
  const [furnitureScale, setFurnitureScale] = useState(0.35);
  const [furnitureRotation, setFurnitureRotation] = useState(0);
  const [furnitureFlipH, setFurnitureFlipH] = useState(false);
  const [furnitureFlipV, setFurnitureFlipV] = useState(false);
  const [furniturePerspX, setFurniturePerspX] = useState(0);
  const [furniturePerspY, setFurniturePerspY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  interface ExtraPiece {
    id: string;
    label: string;
    imageSrc: string;
    x: number;
    y: number;
    scale: number;
    rotation: number;
    flipH: boolean;
    flipV: boolean;
    perspX: number;
    perspY: number;
  }
  const [extraPieces, setExtraPieces] = useState<ExtraPiece[]>([]);
  const [selectedPieceId, setSelectedPieceId] = useState<string | null>(null);
  const extraPieceInputRef = useRef<HTMLInputElement>(null);
  const [showBefore, setShowBefore] = useState(false);
  const [sliderPos, setSliderPos] = useState(50);
  const [isSliding, setIsSliding] = useState(false);
  const [activeAltIndex, setActiveAltIndex] = useState(-1);

  const [pinDropdown, setPinDropdown] = useState(false);
  const [pinSuccess, setPinSuccess] = useState(false);

  const roomRef = useRef<HTMLInputElement>(null);
  const productRef = useRef<HTMLInputElement>(null);
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const timeoutRefs = useRef<ReturnType<typeof setTimeout>[]>([]);
  const rafRef = useRef<number | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      timeoutRefs.current.forEach(clearTimeout);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  const [vizDescription, setVizDescription] = useState("");
  const [vizStyle, setVizStyle] = useState("");
  const [vizFurniture, setVizFurniture] = useState("");
  const [vizColors, setVizColors] = useState<string[]>([]);
  const [isVisualizing, setIsVisualizing] = useState(false);
  const [vizResult, setVizResult] = useState<VisualizeResult | null>(null);
  const [vizError, setVizError] = useState("");
  const [vizRoomPhoto, setVizRoomPhoto] = useState<string | null>(null);
  const [vizRoomPhotoBase64, setVizRoomPhotoBase64] = useState<string | null>(null);
  const [vizStep, setVizStep] = useState(0);
  const [vizConversationHistory, setVizConversationHistory] = useState<{ role: "user" | "ai"; text: string }[]>([]);
  const [vizFollowUpMessage, setVizFollowUpMessage] = useState("");
  const [isVizFollowingUp, setIsVizFollowingUp] = useState(false);
  const [vizConceptBoardUrl, setVizConceptBoardUrl] = useState<string | null>(null);
  const [failedInspImgs, setFailedInspImgs] = useState<Set<number>>(new Set());
  const vizRoomRef = useRef<HTMLInputElement>(null);
  const vizCanvasRef = useRef<HTMLCanvasElement>(null);

  const [aiRoomPhoto, setAiRoomPhoto] = useState<string | null>(null);
  const [aiRoomBase64, setAiRoomBase64] = useState<string | null>(null);
  const [aiFurnPhoto, setAiFurnPhoto] = useState<string | null>(null);
  const [aiFurnBase64, setAiFurnBase64] = useState<string | null>(null);
  const [aiInstructions, setAiInstructions] = useState("");
  const [aiIsGenerating, setAiIsGenerating] = useState(false);
  const [aiResultImage, setAiResultImage] = useState<string | null>(null);
  const [aiError, setAiError] = useState("");
  const [aiHistory, setAiHistory] = useState<{ instructions: string; imageUrl: string }[]>([]);
  const [aiPinDropdown, setAiPinDropdown] = useState(false);
  const [aiPinSuccess, setAiPinSuccess] = useState(false);
  const [stageRoom, setStageRoom] = useState(false);
  const [stagingItems, setStagingItems] = useState<Record<string, boolean>>({
    "area rug": false,
    "coffee table": false,
    "end tables": false,
    "table lamps": false,
    "floor lamp": false,
    "throw pillows": false,
    "throw blanket": false,
    "wall art": false,
    "plants": false,
    "books & decor": false,
    "curtains": false,
    "candles": false,
  });
  const aiRoomRef = useRef<HTMLInputElement>(null);
  const aiFurnRef = useRef<HTMLInputElement>(null);

  const handleRoomUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      setRoomPhoto(dataUrl);
      setRoomPhotoBase64(dataUrl.split(",")[1]);
      setError("");
      if (step === 1) setStep(2);
    };
    reader.readAsDataURL(file);
  };

  const handleAiRoomUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      setAiRoomPhoto(dataUrl);
      setAiRoomBase64(dataUrl.split(",")[1]);
      setAiError("");
    };
    reader.readAsDataURL(file);
  };

  const handleAiFurnUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      setAiFurnPhoto(dataUrl);
      setAiFurnBase64(dataUrl.split(",")[1]);
      setAiError("");
    };
    reader.readAsDataURL(file);
  };

  const generateAiRebuild = async () => {
    if (!aiRoomBase64 || !aiFurnBase64 || !aiInstructions.trim()) return;
    setAiIsGenerating(true);
    setAiError("");
    setAiResultImage(null);
    const selectedStaging = stageRoom
      ? Object.entries(stagingItems).filter(([, v]) => v).map(([k]) => k)
      : [];
    try {
      const resp = await apiRequest<{ imageBase64: string }>("/room/ai-rebuild", {
        method: "POST",
        body: JSON.stringify({
          roomImageBase64: aiRoomBase64,
          furnitureImageBase64: aiFurnBase64,
          instructions: aiInstructions.trim(),
          stagingItems: selectedStaging,
        }),
      });
      if (resp.imageBase64) {
        const imgUrl = `data:image/png;base64,${resp.imageBase64}`;
        setAiResultImage(imgUrl);
        setAiHistory(prev => [{ instructions: aiInstructions.trim(), imageUrl: imgUrl }, ...prev].slice(0, 10));
      }
    } catch (err: any) {
      setAiError(err.message || "Failed to generate AI room rebuild");
    }
    setAiIsGenerating(false);
  };

  const aiPinToCustomer = async (customerId: string) => {
    if (!aiResultImage) return;
    const customer = customers.find((c) => c.id === customerId);
    if (!customer) return;
    const existing = customer.pinnedImages || [];
    const selectedStaging = stageRoom
      ? Object.entries(stagingItems).filter(([, v]) => v).map(([k]) => k)
      : [];
    const label = selectedStaging.length > 0
      ? `AI Room Rebuild (staged: ${selectedStaging.slice(0, 3).join(", ")}${selectedStaging.length > 3 ? "..." : ""})`
      : "AI Room Rebuild";
    await updateCustomer(customerId, {
      pinnedImages: [...existing, { url: aiResultImage, label, createdAt: new Date().toISOString() }],
    });
    setAiPinSuccess(true);
    setAiPinDropdown(false);
    setTimeout(() => setAiPinSuccess(false), 2000);
  };

  const downloadAiResult = () => {
    if (!aiResultImage) return;
    const a = document.createElement("a");
    a.href = aiResultImage;
    a.download = `ai-room-rebuild-${Date.now()}.png`;
    a.click();
  };

  const resetAiRebuild = () => {
    setAiRoomPhoto(null);
    setAiRoomBase64(null);
    setAiFurnPhoto(null);
    setAiFurnBase64(null);
    setAiInstructions("");
    setAiResultImage(null);
    setAiError("");
    setAiPinDropdown(false);
    setAiPinSuccess(false);
  };

  const handleProductUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      setProductPhoto(dataUrl);
      setProductPhotoBase64(dataUrl.split(",")[1]);
      setProductCutout(null);
      setError("");
    };
    reader.readAsDataURL(file);
  };

  const removeBg = async () => {
    if (!productPhoto || isRemovingBg) return;
    setIsRemovingBg(true);
    setError("");
    try {
      const { removeBackground } = await import("@imgly/background-removal");
      const response = await fetch(productPhoto);
      const blob = await response.blob();
      const resultBlob = await removeBackground(blob);
      const cutoutUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(resultBlob);
      });
      setProductCutout(cutoutUrl);
    } catch {
      setError("Background removal failed. You can still proceed — the product photo will be used as-is.");
    } finally {
      setIsRemovingBg(false);
    }
  };

  const generatePlacement = async () => {
    if (!roomPhotoBase64 || (!productPhotoBase64 && !productCutout)) return;
    setIsGenerating(true);
    setError("");
    setGenerateStep(1);
    const t1 = setTimeout(() => { if (mountedRef.current) setGenerateStep(2); }, 3000);
    const t2 = setTimeout(() => { if (mountedRef.current) setGenerateStep(3); }, 8000);
    const t3 = setTimeout(() => { if (mountedRef.current) setGenerateStep(4); }, 14000);
    timeoutRefs.current.push(t1, t2, t3);
    try {
      const furnitureSrc = productCutout || productPhoto!;
      const furnitureB64 = furnitureSrc.includes(",") ? furnitureSrc.split(",")[1] : furnitureSrc;
      const data = await apiRequest<PlacementResult>("/room/placement", {
        method: "POST",
        body: JSON.stringify({
          roomImageBase64: roomPhotoBase64,
          furnitureImageBase64: furnitureB64,
          mode,
          placementPreset,
          furnitureCategory: furnitureCategory || undefined,
        }),
      });
      clearTimeout(t1); clearTimeout(t2); clearTimeout(t3);
      if (!mountedRef.current) return;
      setPlacementResult(data);
      setFurnitureX(data.placement.x);
      setFurnitureY(data.placement.y);
      setFurnitureScale(data.placement.scale);
      setFurnitureRotation(data.placement.rotation || 0);
      setActiveAltIndex(-1);
      setStep(4);
    } catch (err: any) {
      clearTimeout(t1); clearTimeout(t2); clearTimeout(t3);
      if (!mountedRef.current) return;
      setError(err.message || "Failed to generate placement. Please try again.");
    }
    if (mountedRef.current) {
      setGenerateStep(0);
      setIsGenerating(false);
    }
  };

  const cachedImagesRef = useRef<{ roomSrc: string; roomImg: HTMLImageElement; furnitureSrc: string; furnitureImg: HTMLImageElement } | null>(null);

  const drawPieceOnCanvas = (
    ctx: CanvasRenderingContext2D,
    canvasW: number, canvasH: number,
    img: HTMLImageElement,
    px: number, py: number, pScale: number,
    pRotation: number, pFlipH: boolean, pFlipV: boolean,
    pPerspX: number, pPerspY: number,
  ) => {
    const fw = canvasW * pScale;
    const fAspect = img.width / img.height;
    const fh = fw / fAspect;
    const dx = canvasW * px - fw / 2;
    const dy = canvasH * py - fh;
    const needsPersp = pPerspX !== 0 || pPerspY !== 0;

    if (needsPersp) {
      const offscreen = document.createElement("canvas");
      offscreen.width = Math.ceil(fw) + 2;
      offscreen.height = Math.ceil(fh) + 2;
      const oCtx = offscreen.getContext("2d")!;
      oCtx.save();
      const ocx = offscreen.width / 2;
      const ocy = offscreen.height / 2;
      oCtx.translate(ocx, ocy);
      if (pRotation !== 0) oCtx.rotate((pRotation * Math.PI) / 180);
      oCtx.scale(pFlipH ? -1 : 1, pFlipV ? -1 : 1);
      oCtx.translate(-ocx, -ocy);
      oCtx.drawImage(img, 1, 1, fw, fh);
      oCtx.restore();

      const pxF = pPerspX / 100;
      const pyF = pPerspY / 100;
      const strips = Math.max(Math.ceil(fh), 1);
      ctx.save();
      ctx.shadowColor = "rgba(0,0,0,0.25)";
      ctx.shadowBlur = 18;
      ctx.shadowOffsetY = 10;
      ctx.shadowOffsetX = 4;
      for (let i = 0; i < strips; i++) {
        const t = strips > 1 ? i / (strips - 1) : 0.5;
        const yScale = 1 - pxF * (t - 0.5) * 2;
        const xShift = pyF * (t - 0.5) * fw * 0.5;
        const sliceW = fw * Math.abs(yScale);
        const sliceX = dx + (fw - sliceW) / 2 + xShift;
        const sy = 1 + i * (fh / strips);
        const sh = fh / strips;
        if (sliceW <= 0) continue;
        ctx.drawImage(offscreen, 0, sy, offscreen.width, sh, sliceX, dy + i * (fh / strips), sliceW, sh);
        if (i === 0) { ctx.shadowColor = "transparent"; ctx.shadowBlur = 0; ctx.shadowOffsetY = 0; ctx.shadowOffsetX = 0; }
      }
      ctx.restore();
    } else {
      ctx.save();
      const cx = dx + fw / 2;
      const cy = dy + fh / 2;
      ctx.translate(cx, cy);
      if (pRotation !== 0) ctx.rotate((pRotation * Math.PI) / 180);
      ctx.scale(pFlipH ? -1 : 1, pFlipV ? -1 : 1);
      ctx.translate(-cx, -cy);
      ctx.shadowColor = "rgba(0,0,0,0.25)";
      ctx.shadowBlur = 18;
      ctx.shadowOffsetY = 10;
      ctx.shadowOffsetX = 4;
      ctx.drawImage(img, dx, dy, fw, fh);
      ctx.restore();
    }
  };

  const extraImgCacheRef = useRef<Map<string, HTMLImageElement>>(new Map());

  const renderComposite = useCallback(() => {
    if (!roomPhoto || !previewCanvasRef.current) return;
    const canvas = previewCanvasRef.current;
    const ctx = canvas.getContext("2d")!;
    const furnitureSrc = productCutout || productPhoto;
    if (!furnitureSrc) return;

    const drawFrame = (roomImg: HTMLImageElement, furnitureImg: HTMLImageElement) => {
      if (!mountedRef.current) return;
      canvas.width = roomImg.width;
      canvas.height = roomImg.height;
      ctx.drawImage(roomImg, 0, 0);

      drawPieceOnCanvas(ctx, canvas.width, canvas.height, furnitureImg,
        furnitureX, furnitureY, furnitureScale, furnitureRotation,
        furnitureFlipH, furnitureFlipV, furniturePerspX, furniturePerspY);

      let extraLoaded = 0;
      const currentExtras = [...extraPieces];
      if (currentExtras.length === 0) {
        try { setCompositeUrl(canvas.toDataURL("image/png")); } catch { setCompositeUrl(null); }
        return;
      }

      const drawExtras = () => {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(roomImg, 0, 0);
        drawPieceOnCanvas(ctx, canvas.width, canvas.height, furnitureImg,
          furnitureX, furnitureY, furnitureScale, furnitureRotation,
          furnitureFlipH, furnitureFlipV, furniturePerspX, furniturePerspY);
        for (const ep of currentExtras) {
          const eImg = extraImgCacheRef.current.get(ep.id);
          if (eImg && eImg.complete) {
            drawPieceOnCanvas(ctx, canvas.width, canvas.height, eImg,
              ep.x, ep.y, ep.scale, ep.rotation, ep.flipH, ep.flipV, ep.perspX, ep.perspY);
          }
        }
        try { setCompositeUrl(canvas.toDataURL("image/png")); } catch { setCompositeUrl(null); }
      };

      for (const ep of currentExtras) {
        const cached = extraImgCacheRef.current.get(ep.id);
        if (cached && cached.src === ep.imageSrc && cached.complete) {
          extraLoaded++;
          if (extraLoaded === currentExtras.length) drawExtras();
        } else {
          const eImg = new window.Image();
          eImg.crossOrigin = "anonymous";
          eImg.onload = () => {
            extraImgCacheRef.current.set(ep.id, eImg);
            extraLoaded++;
            if (extraLoaded === currentExtras.length) drawExtras();
          };
          eImg.onerror = () => {
            extraLoaded++;
            if (extraLoaded === currentExtras.length) drawExtras();
          };
          eImg.src = ep.imageSrc;
        }
      }
    };

    const cached = cachedImagesRef.current;
    if (cached && cached.roomSrc === roomPhoto && cached.furnitureSrc === furnitureSrc) {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => drawFrame(cached.roomImg, cached.furnitureImg));
      return;
    }

    const roomImg = new window.Image();
    roomImg.crossOrigin = "anonymous";
    const furnitureImg = new window.Image();
    furnitureImg.crossOrigin = "anonymous";
    let loaded = 0;
    const onLoad = () => {
      loaded++;
      if (loaded < 2) return;
      cachedImagesRef.current = { roomSrc: roomPhoto, roomImg, furnitureSrc, furnitureImg };
      drawFrame(roomImg, furnitureImg);
    };
    roomImg.onload = onLoad;
    furnitureImg.onload = onLoad;
    roomImg.onerror = () => setError("Failed to load room image for preview.");
    furnitureImg.onerror = () => setError("Failed to load product image for preview.");
    roomImg.src = roomPhoto;
    furnitureImg.src = furnitureSrc;
  }, [roomPhoto, productPhoto, productCutout, furnitureX, furnitureY, furnitureScale, furnitureRotation, furnitureFlipH, furnitureFlipV, furniturePerspX, furniturePerspY, extraPieces]);

  useEffect(() => {
    if (step === 4 && placementResult) {
      renderComposite();
    }
  }, [step, placementResult, furnitureX, furnitureY, furnitureScale, furnitureRotation, renderComposite]);

  const handlePreviewMouseDown = (e: React.MouseEvent) => {
    if (!containerRef.current) return;
    setIsDragging(true);
    updateFurniturePos(e);
  };

  const handlePreviewMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !containerRef.current) return;
    updateFurniturePos(e);
  };

  const handlePreviewMouseUp = () => setIsDragging(false);

  const updateFurniturePos = (e: React.MouseEvent) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = Math.max(0.05, Math.min(0.95, (e.clientX - rect.left) / rect.width));
    const y = Math.max(0.1, Math.min(0.95, (e.clientY - rect.top) / rect.height));
    setFurnitureX(x);
    setFurnitureY(y);
  };

  const applyAlternative = (alt: { x: number; y: number; scale: number; rotation: number }, idx: number) => {
    setFurnitureX(alt.x);
    setFurnitureY(alt.y);
    setFurnitureScale(alt.scale);
    setFurnitureRotation(alt.rotation || 0);
    setActiveAltIndex(idx);
  };

  const applyOriginal = () => {
    if (!placementResult) return;
    setFurnitureX(placementResult.placement.x);
    setFurnitureY(placementResult.placement.y);
    setFurnitureScale(placementResult.placement.scale);
    setFurnitureRotation(placementResult.placement.rotation || 0);
    setActiveAltIndex(-1);
  };

  const downloadComposite = () => {
    if (!compositeUrl) return;
    const a = document.createElement("a");
    a.href = compositeUrl;
    a.download = `room-staging-${Date.now()}.png`;
    a.click();
  };

  const pinToCustomer = async (customerId: string) => {
    if (!compositeUrl) return;
    const customer = customers.find((c) => c.id === customerId);
    if (!customer) return;
    const existing = customer.pinnedImages || [];
    await updateCustomer(customerId, {
      pinnedImages: [...existing, { url: compositeUrl, label: `Room staging — ${placementResult?.furnitureName || "furniture"}`, createdAt: new Date().toISOString() }],
    });
    setPinSuccess(true);
    setPinDropdown(false);
    setTimeout(() => setPinSuccess(false), 2000);
  };

  const handleExtraPieceUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const src = reader.result as string;
      const id = Date.now().toString() + Math.random().toString(36).substr(2, 5);
      const piece: ExtraPiece = {
        id,
        label: file.name.replace(/\.[^.]+$/, "").slice(0, 20) || `Piece ${extraPieces.length + 1}`,
        imageSrc: src,
        x: 0.3 + Math.random() * 0.4,
        y: 0.5 + Math.random() * 0.3,
        scale: 0.15,
        rotation: 0,
        flipH: false,
        flipV: false,
        perspX: 0,
        perspY: 0,
      };
      setExtraPieces(prev => [...prev, piece]);
      setSelectedPieceId(id);
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const updateExtraPiece = (id: string, updates: Partial<ExtraPiece>) => {
    setExtraPieces(prev => prev.map(p => p.id === id ? { ...p, ...updates } : p));
  };

  const removeExtraPiece = (id: string) => {
    setExtraPieces(prev => prev.filter(p => p.id !== id));
    if (selectedPieceId === id) setSelectedPieceId(null);
  };

  const selectedExtra = extraPieces.find(p => p.id === selectedPieceId) || null;

  const startOver = () => {
    setStep(1);
    setRoomPhoto(null);
    setRoomPhotoBase64(null);
    setProductPhoto(null);
    setProductPhotoBase64(null);
    setProductCutout(null);
    setPlacementResult(null);
    setCompositeUrl(null);
    setError("");
    setActiveAltIndex(-1);
    setMode("replace");
    setFurnitureCategory("");
    setPlacementPreset("center-wall");
    setFurnitureX(0.5);
    setFurnitureY(0.7);
    setFurnitureScale(0.35);
    setFurnitureRotation(0);
    setFurnitureFlipH(false);
    setFurnitureFlipV(false);
    setFurniturePerspX(0);
    setFurniturePerspY(0);
    setExtraPieces([]);
    setSelectedPieceId(null);
    setShowBefore(false);
    setSliderPos(50);
    setPinDropdown(false);
    setPinSuccess(false);
    cachedImagesRef.current = null;
  };

  const handleSliderMove = (e: React.MouseEvent | React.TouchEvent) => {
    if (!isSliding || !containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const clientX = "touches" in e ? e.touches[0].clientX : e.clientX;
    const pct = Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100));
    setSliderPos(pct);
  };

  const toggleColor = (c: string) => setVizColors((prev) => prev.includes(c) ? prev.filter((x) => x !== c) : [...prev, c]);

  const handleVizRoomUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setVizRoomPhoto(reader.result as string);
      setVizRoomPhotoBase64((reader.result as string).split(",")[1]);
    };
    reader.readAsDataURL(file);
  };

  const generateVisuals = async () => {
    if (!vizDescription && !vizStyle && !vizFurniture && !vizRoomPhoto) return;
    setIsVisualizing(true);
    setVizError("");
    setVizConceptBoardUrl(null);
    setVizConversationHistory([]);
    setVizStep(1);
    const st1 = setTimeout(() => { if (mountedRef.current) setVizStep(2); }, 3000);
    const st2 = setTimeout(() => { if (mountedRef.current) setVizStep(3); }, 7000);
    const st3 = setTimeout(() => { if (mountedRef.current) setVizStep(4); }, 12000);
    timeoutRefs.current.push(st1, st2, st3);
    try {
      const body: any = { description: vizDescription, style: vizStyle, furniture: vizFurniture, colors: vizColors.join(", ") };
      if (vizRoomPhotoBase64) body.roomPhotoBase64 = vizRoomPhotoBase64;
      const data = await apiRequest<VisualizeResult>("/room/visualize", { method: "POST", body: JSON.stringify(body) });
      clearTimeout(st1); clearTimeout(st2); clearTimeout(st3);
      setVizResult(data);
      const history: { role: "user" | "ai"; text: string }[] = [];
      const userReq = [vizDescription, vizStyle ? `Style: ${vizStyle}` : "", vizFurniture ? `Furniture: ${vizFurniture}` : ""].filter(Boolean).join(". ");
      if (userReq) history.push({ role: "user", text: userReq });
      history.push({ role: "ai", text: data.analysis?.redesignDescription || data.analysis?.designNotes || data.description || "Room visualization complete." });
      setVizConversationHistory(history);
    } catch (err: any) {
      clearTimeout(st1); clearTimeout(st2); clearTimeout(st3);
      setVizError(err.message);
    }
    setVizStep(0);
    setIsVisualizing(false);
  };

  const sendVizFollowUp = async () => {
    if (!vizFollowUpMessage.trim() || !vizResult?.analysis || isVizFollowingUp) return;
    if (!vizRoomPhotoBase64) return;
    setIsVizFollowingUp(true);
    setVizError("");
    const userMsg = vizFollowUpMessage.trim();
    setVizFollowUpMessage("");
    setVizConversationHistory((prev) => [...prev, { role: "user", text: userMsg }]);
    try {
      const body: any = {
        description: vizDescription, style: vizStyle, furniture: vizFurniture, colors: vizColors.join(", "),
        roomPhotoBase64: vizRoomPhotoBase64, followUpMessage: userMsg, previousAnalysis: vizResult.analysis,
      };
      const data = await apiRequest<VisualizeResult>("/room/visualize", { method: "POST", body: JSON.stringify(body) });
      setVizResult(data);
      setVizConversationHistory((prev) => [...prev, { role: "ai", text: data.analysis?.redesignDescription || data.analysis?.designNotes || "Updated." }]);
    } catch (err: any) {
      setVizError(err.message);
      setVizConversationHistory((prev) => [...prev, { role: "ai", text: "Failed to update. Try again." }]);
    }
    setIsVizFollowingUp(false);
  };

  return (
    <div className="room-rebuild-page">
      <div className="page-header">
        <h1>AI Room Designer</h1>
        <div className="mode-toggle">
          <button className={`mode-btn ${pageMode === "airebuild" ? "active" : ""}`} onClick={() => setPageMode("airebuild")}>
            <Wand2 size={16} /> AI Room Rebuild
          </button>
          <button className={`mode-btn ${pageMode === "visualize" ? "active" : ""}`} onClick={() => setPageMode("visualize")}>
            <Sparkles size={16} /> Room Visualizer
          </button>
        </div>
      </div>

      {pageMode === "rebuild" && (
        <div className="staging-flow">
          <div className="step-indicator">
            {[
              { num: 1, label: "Room Photo" },
              { num: 2, label: "Product Photo" },
              { num: 3, label: "Configure" },
              { num: 4, label: "Preview" },
            ].map((s) => (
              <div key={s.num} className={`step-dot ${step >= s.num ? "active" : ""} ${step === s.num ? "current" : ""}`}>
                <span className="step-num">{step > s.num ? <Check size={14} /> : s.num}</span>
                <span className="step-label">{s.label}</span>
              </div>
            ))}
          </div>

          {error && <div className="error-banner">{error}</div>}

          {step === 1 && (
            <div className="step-content">
              <div className="card upload-card">
                <h2><Camera size={20} /> Upload Customer's Room Photo</h2>
                <p className="upload-hint">Take a photo of the customer's room or upload one from their phone</p>
                <div className="upload-zone" onClick={() => roomRef.current?.click()}>
                  {roomPhoto ? (
                    <img src={roomPhoto} alt="Room" className="upload-preview" />
                  ) : (
                    <div className="upload-placeholder">
                      <Upload size={40} />
                      <span>Click to upload room photo</span>
                      <span className="upload-formats">JPG, PNG — any room photo</span>
                    </div>
                  )}
                </div>
                <input ref={roomRef} type="file" accept="image/*" onChange={handleRoomUpload} hidden />
                {roomPhoto && (
                  <div className="step-actions">
                    <button className="btn btn-secondary" onClick={() => { setRoomPhoto(null); setRoomPhotoBase64(null); }}>Change Photo</button>
                    <button className="btn btn-primary" onClick={() => setStep(2)}>Next: Upload Product <ChevronRight size={16} /></button>
                  </div>
                )}
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="step-content">
              <div className="step-context">
                <img src={roomPhoto!} alt="Room" className="context-thumb" />
                <span>Room photo ready</span>
              </div>
              <div className="card upload-card">
                <h2><Sofa size={20} /> Upload Product Photo</h2>
                <p className="upload-hint">Upload a photo of the furniture piece from your showroom or catalog</p>
                <div className="upload-zone" onClick={() => productRef.current?.click()}>
                  {productPhoto ? (
                    <div className="product-preview-wrap">
                      <img src={productCutout || productPhoto} alt="Product" className="upload-preview" />
                      {productCutout && <span className="bg-removed-badge"><Check size={12} /> Background Removed</span>}
                    </div>
                  ) : (
                    <div className="upload-placeholder">
                      <Upload size={40} />
                      <span>Click to upload product photo</span>
                      <span className="upload-formats">Sofa, sectional, bed, table, etc.</span>
                    </div>
                  )}
                </div>
                <input ref={productRef} type="file" accept="image/*" onChange={handleProductUpload} hidden />
                {productPhoto && (
                  <div className="product-actions">
                    {!productCutout && (
                      <button className="btn btn-secondary" onClick={removeBg} disabled={isRemovingBg}>
                        {isRemovingBg ? <><Loader2 size={14} className="spinning" /> Removing Background...</> : <><Wand2 size={14} /> Remove Background</>}
                      </button>
                    )}
                    {productCutout && (
                      <button className="btn btn-secondary" onClick={() => setProductCutout(null)}>Use Original Photo</button>
                    )}
                  </div>
                )}
                {productPhoto && (
                  <div className="step-actions">
                    <button className="btn btn-secondary" onClick={() => setStep(1)}><ArrowLeft size={14} /> Back</button>
                    <button className="btn btn-primary" onClick={() => setStep(3)}>Next: Configure <ChevronRight size={16} /></button>
                  </div>
                )}
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="step-content">
              <div className="step-context">
                <img src={roomPhoto!} alt="Room" className="context-thumb" />
                <img src={productCutout || productPhoto!} alt="Product" className="context-thumb product-thumb" />
              </div>
              <div className="card config-card">
                <h2><Wand2 size={20} /> Configure Placement</h2>

                <div className="config-section">
                  <label className="config-label">Mode</label>
                  <div className="mode-chips">
                    <button className={`chip ${mode === "replace" ? "active" : ""}`} onClick={() => setMode("replace")}>
                      <Replace size={14} /> Replace Existing
                    </button>
                    <button className={`chip ${mode === "add" ? "active" : ""}`} onClick={() => setMode("add")}>
                      <Plus size={14} /> Add to Room
                    </button>
                  </div>
                  <span className="config-hint">{mode === "replace" ? "AI will find existing furniture to replace" : "AI will find the best open space"}</span>
                </div>

                <div className="config-section">
                  <label className="config-label">Furniture Category</label>
                  <div className="category-chips">
                    {FURNITURE_CATEGORIES.map((cat) => (
                      <button key={cat} className={`chip sm ${furnitureCategory === cat ? "active" : ""}`}
                        onClick={() => setFurnitureCategory(furnitureCategory === cat ? "" : cat)}>{cat}</button>
                    ))}
                  </div>
                </div>

                <div className="config-section">
                  <label className="config-label">Placement Preference</label>
                  <div className="preset-grid">
                    {PLACEMENT_PRESETS.map((p) => (
                      <button key={p.id} className={`preset-btn ${placementPreset === p.id ? "active" : ""}`}
                        onClick={() => setPlacementPreset(p.id)}>
                        <p.icon size={18} />
                        <span>{p.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="step-actions">
                  <button className="btn btn-secondary" onClick={() => setStep(2)}><ArrowLeft size={14} /> Back</button>
                  <button className="btn btn-primary btn-lg" onClick={generatePlacement} disabled={isGenerating}>
                    {isGenerating ? <><Loader2 size={16} className="spinning" /> Generating...</> : <><Wand2 size={16} /> Generate Preview</>}
                  </button>
                </div>
              </div>

              {isGenerating && (
                <div className="card ai-progress-card" style={{ marginTop: 20 }}>
                  <h3><Sparkles size={16} /> AI Staging in Progress</h3>
                  <div className={`ai-progress-step ${generateStep >= 1 ? "active" : ""} ${generateStep > 1 ? "done" : ""}`}>
                    <div className="ai-step-icon">{generateStep > 1 ? <Check size={16} /> : generateStep === 1 ? <Loader2 size={16} className="spinning" /> : <Camera size={16} />}</div>
                    <div><strong>Analyzing Room</strong><br /><span>Detecting walls, floor, perspective, existing furniture</span></div>
                  </div>
                  <div className={`ai-progress-step ${generateStep >= 2 ? "active" : ""} ${generateStep > 2 ? "done" : ""}`}>
                    <div className="ai-step-icon">{generateStep > 2 ? <Check size={16} /> : generateStep === 2 ? <Loader2 size={16} className="spinning" /> : <Sofa size={16} />}</div>
                    <div><strong>Analyzing Product</strong><br /><span>Measuring scale, identifying type, matching perspective</span></div>
                  </div>
                  <div className={`ai-progress-step ${generateStep >= 3 ? "active" : ""} ${generateStep > 3 ? "done" : ""}`}>
                    <div className="ai-step-icon">{generateStep > 3 ? <Check size={16} /> : generateStep === 3 ? <Loader2 size={16} className="spinning" /> : <Move size={16} />}</div>
                    <div><strong>Calculating Placement</strong><br /><span>Finding optimal position, scale, and orientation</span></div>
                  </div>
                  <div className={`ai-progress-step ${generateStep >= 4 ? "active" : ""}`}>
                    <div className="ai-step-icon">{generateStep === 4 ? <Loader2 size={16} className="spinning" /> : <Layers size={16} />}</div>
                    <div><strong>Compositing Preview</strong><br /><span>Blending furniture with room, adding shadows</span></div>
                  </div>
                </div>
              )}
            </div>
          )}

          {step === 4 && placementResult && (
            <div className="step-content preview-step">
              <div className="preview-header">
                <h2><Eye size={20} /> Room Preview</h2>
                <div className="preview-header-actions">
                  <button className={`btn btn-sm ${showBefore ? "btn-primary" : "btn-secondary"}`}
                    onClick={() => setShowBefore(!showBefore)}>
                    {showBefore ? "Show Staged" : "Before / After"}
                  </button>
                  <button className="btn btn-secondary btn-sm" onClick={() => { setStep(3); setPlacementResult(null); setCompositeUrl(null); }}>
                    <RefreshCw size={14} /> Regenerate
                  </button>
                  <button className="btn btn-primary btn-sm" onClick={downloadComposite} disabled={!compositeUrl}>
                    <Download size={14} /> Download
                  </button>
                </div>
              </div>

              <div className="preview-layout">
                <div className="preview-main">
                  {showBefore ? (
                    <div className="before-after-container" ref={containerRef}
                      onMouseDown={() => setIsSliding(true)}
                      onMouseUp={() => setIsSliding(false)}
                      onMouseLeave={() => setIsSliding(false)}
                      onMouseMove={handleSliderMove}
                      onTouchStart={() => setIsSliding(true)}
                      onTouchEnd={() => setIsSliding(false)}
                      onTouchMove={handleSliderMove}>
                      <div className="ba-before" style={{ clipPath: `inset(0 ${100 - sliderPos}% 0 0)` }}>
                        <img src={roomPhoto!} alt="Before" />
                        <span className="ba-label ba-label-before">BEFORE</span>
                      </div>
                      <div className="ba-after" style={{ clipPath: `inset(0 0 0 ${sliderPos}%)` }}>
                        {compositeUrl ? <img src={compositeUrl} alt="After" /> : <img src={roomPhoto!} alt="Room" />}
                        <span className="ba-label ba-label-after">AFTER</span>
                      </div>
                      <div className="ba-slider" style={{ left: `${sliderPos}%` }}>
                        <div className="ba-slider-handle">
                          <ArrowLeft size={12} /><ArrowRight size={12} />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="interactive-preview" ref={containerRef}
                      onMouseDown={handlePreviewMouseDown}
                      onMouseMove={handlePreviewMouseMove}
                      onMouseUp={handlePreviewMouseUp}
                      onMouseLeave={handlePreviewMouseUp}>
                      {compositeUrl ? (
                        <img src={compositeUrl} alt="Staged Room" className="preview-image" />
                      ) : (
                        <img src={roomPhoto!} alt="Room" className="preview-image" />
                      )}
                      <div className="drag-hint">Click anywhere to reposition furniture</div>
                    </div>
                  )}

                  <div className="placement-controls">
                    <div className="control-group">
                      <label><ZoomIn size={12} /> Scale</label>
                      <input type="range" min="0.05" max="0.8" step="0.01" value={furnitureScale}
                        onChange={(e) => setFurnitureScale(parseFloat(e.target.value))} />
                      <span>{Math.round(furnitureScale * 100)}%</span>
                    </div>
                    <div className="control-group">
                      <label><RotateCw size={12} /> Rotation</label>
                      <input type="range" min="-180" max="180" step="1" value={furnitureRotation}
                        onChange={(e) => setFurnitureRotation(parseInt(e.target.value))} />
                      <span>{furnitureRotation}°</span>
                    </div>
                    <div className="control-group flip-controls">
                      <label><Replace size={12} /> Flip</label>
                      <div style={{ display: "flex", gap: 6, flex: 1 }}>
                        <button
                          className={`btn btn-sm flip-btn ${furnitureFlipH ? "flip-active" : ""}`}
                          onClick={() => setFurnitureFlipH(!furnitureFlipH)}
                          title="Flip Horizontal"
                        >
                          ↔ Horizontal
                        </button>
                        <button
                          className={`btn btn-sm flip-btn ${furnitureFlipV ? "flip-active" : ""}`}
                          onClick={() => setFurnitureFlipV(!furnitureFlipV)}
                          title="Flip Vertical"
                        >
                          ↕ Vertical
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="placement-controls" style={{ marginTop: 4 }}>
                    <div className="control-group">
                      <label><Maximize size={12} /> Perspective L/R</label>
                      <input type="range" min="-60" max="60" step="1" value={furniturePerspX}
                        onChange={(e) => setFurniturePerspX(parseInt(e.target.value))} />
                      <span>{furniturePerspX > 0 ? `R${furniturePerspX}` : furniturePerspX < 0 ? `L${Math.abs(furniturePerspX)}` : "0"}</span>
                    </div>
                    <div className="control-group">
                      <label><Maximize size={12} /> Perspective F/B</label>
                      <input type="range" min="-60" max="60" step="1" value={furniturePerspY}
                        onChange={(e) => setFurniturePerspY(parseInt(e.target.value))} />
                      <span>{furniturePerspY > 0 ? `F${furniturePerspY}` : furniturePerspY < 0 ? `B${Math.abs(furniturePerspY)}` : "0"}</span>
                    </div>
                  </div>

                  <input type="file" accept="image/*" ref={extraPieceInputRef} style={{ display: "none" }} onChange={handleExtraPieceUpload} />
                  <div className="extra-pieces-section">
                    <div className="extra-pieces-header">
                      <h4 style={{ margin: 0, fontSize: 13, color: "#E2E8F0", display: "flex", alignItems: "center", gap: 6 }}>
                        <Layers size={14} /> Additional Furniture ({extraPieces.length})
                      </h4>
                      <button className="btn btn-sm" onClick={() => extraPieceInputRef.current?.click()}
                        style={{ fontSize: 11, padding: "4px 10px", background: "rgba(139,92,246,0.15)", color: "#A78BFA", border: "1px solid rgba(139,92,246,0.3)" }}>
                        <Plus size={12} /> Add Piece
                      </button>
                    </div>

                    {extraPieces.length > 0 && (
                      <div className="extra-pieces-list">
                        {extraPieces.map((ep) => (
                          <div key={ep.id}
                            className={`extra-piece-item ${selectedPieceId === ep.id ? "extra-piece-selected" : ""}`}
                            onClick={() => setSelectedPieceId(selectedPieceId === ep.id ? null : ep.id)}>
                            <img src={ep.imageSrc} alt={ep.label} className="extra-piece-thumb" />
                            <span className="extra-piece-label">{ep.label}</span>
                            <button className="extra-piece-remove" onClick={(e) => { e.stopPropagation(); removeExtraPiece(ep.id); }}
                              title="Remove piece"><X size={12} /></button>
                          </div>
                        ))}
                      </div>
                    )}

                    {selectedExtra && (
                      <div className="extra-piece-controls">
                        <p style={{ fontSize: 11, color: "#A78BFA", fontWeight: 600, marginBottom: 8 }}>
                          Editing: {selectedExtra.label}
                        </p>
                        <div className="control-group">
                          <label><ZoomIn size={12} /> Scale</label>
                          <input type="range" min="0.03" max="0.6" step="0.005" value={selectedExtra.scale}
                            onChange={(e) => updateExtraPiece(selectedExtra.id, { scale: parseFloat(e.target.value) })} />
                          <span>{Math.round(selectedExtra.scale * 100)}%</span>
                        </div>
                        <div className="control-group">
                          <label><RotateCw size={12} /> Rotate</label>
                          <input type="range" min="-180" max="180" step="1" value={selectedExtra.rotation}
                            onChange={(e) => updateExtraPiece(selectedExtra.id, { rotation: parseInt(e.target.value) })} />
                          <span>{selectedExtra.rotation}°</span>
                        </div>
                        <div className="control-group">
                          <label><Move size={12} /> Horiz</label>
                          <input type="range" min="0" max="1" step="0.005" value={selectedExtra.x}
                            onChange={(e) => updateExtraPiece(selectedExtra.id, { x: parseFloat(e.target.value) })} />
                          <span>{Math.round(selectedExtra.x * 100)}</span>
                        </div>
                        <div className="control-group">
                          <label><Move size={12} /> Vert</label>
                          <input type="range" min="0.1" max="1" step="0.005" value={selectedExtra.y}
                            onChange={(e) => updateExtraPiece(selectedExtra.id, { y: parseFloat(e.target.value) })} />
                          <span>{Math.round(selectedExtra.y * 100)}</span>
                        </div>
                        <div className="control-group">
                          <label><Maximize size={12} /> Persp L/R</label>
                          <input type="range" min="-60" max="60" step="1" value={selectedExtra.perspX}
                            onChange={(e) => updateExtraPiece(selectedExtra.id, { perspX: parseInt(e.target.value) })} />
                          <span>{selectedExtra.perspX}</span>
                        </div>
                        <div className="control-group">
                          <label><Maximize size={12} /> Persp F/B</label>
                          <input type="range" min="-60" max="60" step="1" value={selectedExtra.perspY}
                            onChange={(e) => updateExtraPiece(selectedExtra.id, { perspY: parseInt(e.target.value) })} />
                          <span>{selectedExtra.perspY}</span>
                        </div>
                        <div style={{ display: "flex", gap: 6 }}>
                          <button className={`btn btn-sm flip-btn ${selectedExtra.flipH ? "flip-active" : ""}`}
                            onClick={() => updateExtraPiece(selectedExtra.id, { flipH: !selectedExtra.flipH })}>
                            ↔ Flip H
                          </button>
                          <button className={`btn btn-sm flip-btn ${selectedExtra.flipV ? "flip-active" : ""}`}
                            onClick={() => updateExtraPiece(selectedExtra.id, { flipV: !selectedExtra.flipV })}>
                            ↕ Flip V
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="preview-sidebar">
                  <div className="card sidebar-card">
                    <h3><Lightbulb size={14} /> AI Placement</h3>
                    <p className="placement-advice">{placementResult.placementAdvice}</p>
                    <div className="style-match">
                      <span className={`match-badge match-${placementResult.styleMatch}`}>{placementResult.styleMatch}</span>
                      <span>style match</span>
                    </div>
                  </div>

                  {(placementResult.alternativePlacements?.length ?? 0) > 0 && (
                    <div className="card sidebar-card">
                      <h3><Move size={14} /> Alternative Positions</h3>
                      <div className="alt-placements">
                        <button className={`alt-btn ${activeAltIndex === -1 ? "active" : ""}`} onClick={applyOriginal}>
                          AI Recommended
                        </button>
                        {placementResult.alternativePlacements!.map((alt, i) => (
                          <button key={i} className={`alt-btn ${activeAltIndex === i ? "active" : ""}`}
                            onClick={() => applyAlternative(alt, i)}>
                            {alt.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {placementResult.complementaryItems && placementResult.complementaryItems.length > 0 && (
                    <div className="card sidebar-card">
                      <h3><ShoppingCart size={14} /> Complement With</h3>
                      <ul className="complementary-list">
                        {placementResult.complementaryItems.map((item, i) => (
                          <li key={i}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="card sidebar-card">
                    <h3><Info size={14} /> Room Details</h3>
                    <div className="detail-row"><span>Furniture:</span><span>{placementResult.furnitureName}</span></div>
                    <div className="detail-row"><span>Room:</span><span>{placementResult.roomType}</span></div>
                    {placementResult.roomAnalysis?.replacingItem && placementResult.roomAnalysis.replacingItem !== "none" && (
                      <div className="detail-row"><span>Replacing:</span><span>{placementResult.roomAnalysis.replacingItem}</span></div>
                    )}
                    {placementResult.shadowDirection && (
                      <div className="detail-row"><span>Shadow:</span><span>{placementResult.shadowDirection}</span></div>
                    )}
                  </div>

                  <div className="sidebar-actions">
                    {pinSuccess ? (
                      <div className="pin-success-msg"><Check size={14} /> Saved to customer!</div>
                    ) : pinDropdown ? (
                      <div className="pin-customer-dropdown">
                        <div className="pin-dropdown-header">
                          <span>Save to customer:</span>
                          <button onClick={() => setPinDropdown(false)}><X size={14} /></button>
                        </div>
                        {customers.map((c) => (
                          <button key={c.id} className="pin-customer-btn" onClick={() => pinToCustomer(c.id)}>
                            <User size={14} /> {c.name}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <button className="btn btn-secondary btn-block" onClick={() => setPinDropdown(true)}>
                        <Pin size={14} /> Save to Customer
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {placementResult.stagingDescription && (
                <div className="card staging-description-card">
                  <h3><Palette size={16} /> AI Staging Description</h3>
                  <div className="staging-text">{placementResult.stagingDescription}</div>
                </div>
              )}

              <button className="btn btn-secondary" onClick={startOver} style={{ marginTop: 16 }}>
                Start Over
              </button>
            </div>
          )}

          <canvas ref={previewCanvasRef} style={{ display: "none" }} />
        </div>
      )}

      {pageMode === "airebuild" && (
        <div className="ai-rebuild-section">
          <input type="file" ref={aiRoomRef} accept="image/*" onChange={handleAiRoomUpload} style={{ display: "none" }} />
          <input type="file" ref={aiFurnRef} accept="image/*" onChange={handleAiFurnUpload} style={{ display: "none" }} />

          {!aiResultImage && !aiIsGenerating && (
            <div className="ai-rebuild-setup">
              <div className="ai-rebuild-uploads" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>
                <div className="card" style={{ textAlign: "center" }}>
                  <h3 style={{ marginBottom: 12, fontSize: 15 }}><Camera size={16} /> Step 1: Room Photo</h3>
                  {aiRoomPhoto ? (
                    <div style={{ position: "relative" }}>
                      <img src={aiRoomPhoto} alt="Room" style={{ width: "100%", borderRadius: 10, maxHeight: 280, objectFit: "cover" }} />
                      <button className="btn btn-secondary btn-sm" onClick={() => { setAiRoomPhoto(null); setAiRoomBase64(null); }}
                        style={{ position: "absolute", top: 8, right: 8, padding: "4px 8px" }}>
                        <X size={14} />
                      </button>
                    </div>
                  ) : (
                    <div className="upload-zone" onClick={() => aiRoomRef.current?.click()}
                      style={{ padding: "40px 20px", cursor: "pointer", border: "2px dashed rgba(139,92,246,0.3)", borderRadius: 12, background: "rgba(15,23,42,0.4)" }}>
                      <Upload size={32} style={{ color: "#8B5CF6", marginBottom: 8 }} />
                      <p style={{ color: "#94A3B8", fontSize: 14, margin: 0 }}>Upload your room photo</p>
                    </div>
                  )}
                </div>

                <div className="card" style={{ textAlign: "center" }}>
                  <h3 style={{ marginBottom: 12, fontSize: 15 }}><Sofa size={16} /> Step 2: Furniture Photo</h3>
                  {aiFurnPhoto ? (
                    <div style={{ position: "relative" }}>
                      <img src={aiFurnPhoto} alt="Furniture" style={{ width: "100%", borderRadius: 10, maxHeight: 280, objectFit: "cover" }} />
                      <button className="btn btn-secondary btn-sm" onClick={() => { setAiFurnPhoto(null); setAiFurnBase64(null); }}
                        style={{ position: "absolute", top: 8, right: 8, padding: "4px 8px" }}>
                        <X size={14} />
                      </button>
                    </div>
                  ) : (
                    <div className="upload-zone" onClick={() => aiFurnRef.current?.click()}
                      style={{ padding: "40px 20px", cursor: "pointer", border: "2px dashed rgba(139,92,246,0.3)", borderRadius: 12, background: "rgba(15,23,42,0.4)" }}>
                      <Upload size={32} style={{ color: "#8B5CF6", marginBottom: 8 }} />
                      <p style={{ color: "#94A3B8", fontSize: 14, margin: 0 }}>Upload furniture photo</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="card">
                <h3 style={{ marginBottom: 12, fontSize: 15 }}><MessageSquare size={16} /> Step 3: Placement Instructions</h3>
                <textarea
                  value={aiInstructions}
                  onChange={(e) => setAiInstructions(e.target.value)}
                  className="input"
                  rows={3}
                  style={{ fontSize: 14, resize: "vertical", width: "100%", marginBottom: 16 }}
                  placeholder="e.g. Place this sofa along the wall with the windows. Center it between the two windows."
                />
                <div style={{ marginBottom: 16, padding: "14px 16px", background: "rgba(139,92,246,0.08)", border: "1px solid rgba(139,92,246,0.2)", borderRadius: 10 }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", marginBottom: stageRoom ? 14 : 0 }}>
                    <input type="checkbox" checked={stageRoom} onChange={(e) => setStageRoom(e.target.checked)}
                      style={{ width: 18, height: 18, accentColor: "#8B5CF6", cursor: "pointer" }} />
                    <span style={{ fontWeight: 600, fontSize: 14, color: "#E2E8F0" }}>Stage the Room with Accessories</span>
                  </label>
                  {stageRoom && (
                    <>
                      <p style={{ fontSize: 12, color: "#94A3B8", marginBottom: 12 }}>Select items to add around the main furniture piece:</p>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px 16px" }}>
                        {Object.entries(stagingItems).map(([item, checked]) => (
                          <label key={item} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", padding: "4px 0" }}>
                            <input type="checkbox" checked={checked}
                              onChange={(e) => setStagingItems(prev => ({ ...prev, [item]: e.target.checked }))}
                              style={{ width: 15, height: 15, accentColor: "#8B5CF6", cursor: "pointer" }} />
                            <span style={{ fontSize: 13, color: "#CBD5E1", textTransform: "capitalize" }}>{item}</span>
                          </label>
                        ))}
                      </div>
                      <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
                        <button className="btn" onClick={() => setStagingItems(prev => Object.fromEntries(Object.keys(prev).map(k => [k, true])))}
                          style={{ fontSize: 11, padding: "4px 10px", background: "rgba(139,92,246,0.15)", color: "#A78BFA", border: "1px solid rgba(139,92,246,0.3)" }}>
                          Select All
                        </button>
                        <button className="btn" onClick={() => setStagingItems(prev => Object.fromEntries(Object.keys(prev).map(k => [k, false])))}
                          style={{ fontSize: 11, padding: "4px 10px", background: "rgba(100,116,139,0.15)", color: "#94A3B8", border: "1px solid rgba(100,116,139,0.3)" }}>
                          Clear All
                        </button>
                      </div>
                    </>
                  )}
                </div>

                {aiError && <p style={{ color: "#F87171", fontSize: 13, marginBottom: 12 }}>{aiError}</p>}
                <button
                  className="btn btn-primary"
                  onClick={generateAiRebuild}
                  disabled={!aiRoomBase64 || !aiFurnBase64 || !aiInstructions.trim() || aiIsGenerating}
                  style={{ width: "100%" }}
                >
                  <Wand2 size={16} /> {stageRoom ? "Generate Staged Room" : "Generate AI Room Rebuild"}
                </button>
                {(!aiRoomBase64 || !aiFurnBase64) && (
                  <p style={{ color: "#64748B", fontSize: 12, marginTop: 8, textAlign: "center" }}>
                    Upload both photos above to enable generation
                  </p>
                )}
              </div>
            </div>
          )}

          {aiIsGenerating && (
            <div className="card" style={{ textAlign: "center", padding: "60px 20px" }}>
              <div className="loading-spinner" style={{ marginBottom: 16 }} />
              <h3 style={{ color: "#E2E8F0", marginBottom: 8 }}>Generating Your AI Room Rebuild...</h3>
              <p style={{ color: "#94A3B8", fontSize: 14, marginBottom: 4 }}>
                AI is analyzing your room and furniture to create a photorealistic composite
              </p>
              <p style={{ color: "#64748B", fontSize: 12 }}>
                This takes 45-90 seconds for best quality
              </p>
            </div>
          )}

          {aiResultImage && !aiIsGenerating && (
            <div>
              <div className="card" style={{ marginBottom: 16 }}>
                <div className="card-header-row">
                  <h3><Wand2 size={16} /> AI Room Rebuild Result</h3>
                  <div style={{ display: "flex", gap: 6 }}>
                    <button className="btn btn-secondary btn-sm" onClick={downloadAiResult}>
                      <Download size={14} /> Download
                    </button>
                    <button className="btn btn-secondary btn-sm" onClick={resetAiRebuild}>
                      <RefreshCw size={14} /> Start Over
                    </button>
                  </div>
                </div>
                <img src={aiResultImage} alt="AI Room Rebuild" style={{ width: "100%", borderRadius: 12, marginTop: 12 }} />

                <div style={{ marginTop: 16 }}>
                  {aiPinSuccess ? (
                    <div className="pin-success-msg"><Check size={14} /> Saved to customer!</div>
                  ) : aiPinDropdown ? (
                    <div className="pin-customer-dropdown">
                      <div className="pin-dropdown-header">
                        <span>Save to customer:</span>
                        <button onClick={() => setAiPinDropdown(false)}><X size={14} /></button>
                      </div>
                      {customers.length === 0 ? (
                        <p style={{ fontSize: 13, color: "#94A3B8", padding: "8px 12px" }}>No customers yet. Add customers in the CRM first.</p>
                      ) : (
                        customers.map((c) => (
                          <button key={c.id} className="pin-customer-btn" onClick={() => aiPinToCustomer(c.id)}>
                            <User size={14} /> {c.name}
                          </button>
                        ))
                      )}
                    </div>
                  ) : (
                    <button className="btn btn-secondary" onClick={() => setAiPinDropdown(true)} style={{ width: "100%" }}>
                      <Pin size={14} /> Save to Customer Profile
                    </button>
                  )}
                </div>
              </div>

              <div className="card" style={{ marginBottom: 16 }}>
                <h3 style={{ marginBottom: 12, fontSize: 15 }}><MessageSquare size={16} /> Try Different Placement</h3>
                <textarea
                  value={aiInstructions}
                  onChange={(e) => setAiInstructions(e.target.value)}
                  className="input"
                  rows={2}
                  style={{ fontSize: 14, resize: "vertical", width: "100%", marginBottom: 12 }}
                  placeholder="e.g. Move it to the opposite wall instead, angled towards the TV"
                />
                <button className="btn btn-primary" onClick={generateAiRebuild}
                  disabled={!aiInstructions.trim() || aiIsGenerating} style={{ width: "100%" }}>
                  <Wand2 size={16} /> Regenerate with New Instructions
                </button>
              </div>

              {aiHistory.length > 1 && (
                <div className="card">
                  <h3 style={{ marginBottom: 12, fontSize: 15 }}><Layers size={16} /> Previous Results</h3>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: 10 }}>
                    {aiHistory.slice(1).map((h, i) => (
                      <div key={i} style={{ cursor: "pointer", borderRadius: 8, overflow: "hidden", border: "1px solid rgba(139,92,246,0.2)" }}
                        onClick={() => setAiResultImage(h.imageUrl)}>
                        <img src={h.imageUrl} alt={`Result ${i + 1}`} style={{ width: "100%", height: 100, objectFit: "cover" }} />
                        <p style={{ fontSize: 11, color: "#94A3B8", padding: "6px 8px", margin: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {h.instructions}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginTop: 16 }}>
                <div className="card" style={{ textAlign: "center" }}>
                  <p style={{ fontSize: 12, color: "#8B5CF6", fontWeight: 600, marginBottom: 8 }}>ROOM PHOTO</p>
                  {aiRoomPhoto && <img src={aiRoomPhoto} alt="Room" style={{ width: "100%", borderRadius: 8, maxHeight: 200, objectFit: "cover" }} />}
                </div>
                <div className="card" style={{ textAlign: "center" }}>
                  <p style={{ fontSize: 12, color: "#8B5CF6", fontWeight: 600, marginBottom: 8 }}>FURNITURE PHOTO</p>
                  {aiFurnPhoto && <img src={aiFurnPhoto} alt="Furniture" style={{ width: "100%", borderRadius: 8, maxHeight: 200, objectFit: "cover" }} />}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {pageMode === "visualize" && (
        <div className="visualizer-section">
          {!vizResult && !isVisualizing && (
            <div className="card">
              <h2><Sparkles size={20} /> Room Visualizer</h2>
              <p className="viz-desc">Describe your dream room and our AI will create a design vision with Ashley Furniture recommendations.</p>

              <div className="viz-upload-section">
                <label className="config-label"><Camera size={14} /> Room Photo</label>
                <div className="viz-room-upload" onClick={() => vizRoomRef.current?.click()}>
                  {vizRoomPhoto ? (
                    <img src={vizRoomPhoto} alt="Customer Room" className="viz-room-thumb" />
                  ) : (
                    <div className="upload-placeholder small"><Upload size={24} /><span>Upload room photo for personalized analysis</span></div>
                  )}
                </div>
                <input ref={vizRoomRef} type="file" accept="image/*" onChange={handleVizRoomUpload} hidden />
              </div>

              <div className="viz-form">
                <div className="form-group">
                  <label>Describe your vision</label>
                  <textarea placeholder="e.g., 'Modern living room with a large sectional, warm tones, and plenty of natural light...'" value={vizDescription} onChange={(e) => setVizDescription(e.target.value)} rows={3} />
                </div>
                <div className="form-group">
                  <label>Style</label>
                  <div className="chip-group">
                    {STYLE_OPTIONS.map((s) => (
                      <button key={s} className={`chip sm ${vizStyle === s ? "active" : ""}`} onClick={() => setVizStyle(vizStyle === s ? "" : s)}>{s}</button>
                    ))}
                  </div>
                </div>
                <div className="form-group">
                  <label>Main Furniture</label>
                  <div className="chip-group">
                    {FURNITURE_OPTIONS.map((f) => (
                      <button key={f} className={`chip sm ${vizFurniture === f ? "active" : ""}`} onClick={() => setVizFurniture(vizFurniture === f ? "" : f)}>{f}</button>
                    ))}
                  </div>
                </div>
                <div className="form-group">
                  <label>Colors</label>
                  <div className="chip-group">
                    {COLOR_OPTIONS.map((c) => (
                      <button key={c} className={`chip sm ${vizColors.includes(c) ? "active" : ""}`} onClick={() => toggleColor(c)}>{c}</button>
                    ))}
                  </div>
                </div>
              </div>

              <button className="btn btn-primary btn-lg" onClick={generateVisuals}
                disabled={isVisualizing || (!vizDescription && !vizStyle && !vizFurniture && !vizRoomPhoto)}>
                <Sparkles size={16} /> Generate Room Vision
              </button>
            </div>
          )}

          {isVisualizing && (
            <div className="card ai-progress-card">
              <h3><Sparkles size={16} /> Generating Room Visuals</h3>
              {[
                { s: 1, label: "Analyzing Room", desc: "Understanding space, style, and layout", icon: Camera },
                { s: 2, label: "Selecting Furniture", desc: "Matching Ashley products to your vision", icon: Sofa },
                { s: 3, label: "Planning Design", desc: "Creating placement and color harmony", icon: Layers },
                { s: 4, label: "Creating Vision", desc: "Generating your personalized room design", icon: Wand2 },
              ].map(({ s, label, desc, icon: Icon }) => (
                <div key={s} className={`ai-progress-step ${vizStep >= s ? "active" : ""} ${vizStep > s ? "done" : ""}`}>
                  <div className="ai-step-icon">{vizStep > s ? <Check size={16} /> : vizStep === s ? <Loader2 size={16} className="spinning" /> : <Icon size={16} />}</div>
                  <div><strong>{label}</strong><br /><span>{desc}</span></div>
                </div>
              ))}
            </div>
          )}

          {vizError && <div className="error-banner">{vizError}</div>}

          {pageMode === "visualize" && vizResult && (
            <div className="viz-results">
              {vizResult.analysis?.roomAnalysis && (
                <div className="card" style={{ marginBottom: 20 }}>
                  <h3><Sofa size={16} /> Room Analysis</h3>
                  <div className="analysis-grid">
                    <div className="analysis-item"><span className="analysis-label">Room Type</span><span className="analysis-value">{vizResult.analysis.roomAnalysis.roomType || "Unknown"}</span></div>
                    <div className="analysis-item"><span className="analysis-label">Current Style</span><span className="analysis-value">{vizResult.analysis.roomAnalysis.currentStyle || "Unknown"}</span></div>
                    {vizResult.analysis.roomAnalysis.dimensions && <div className="analysis-item"><span className="analysis-label">Est. Dimensions</span><span className="analysis-value">{vizResult.analysis.roomAnalysis.dimensions}</span></div>}
                    {vizResult.analysis.roomAnalysis.wallColor && <div className="analysis-item"><span className="analysis-label">Wall Color</span><span className="analysis-value">{vizResult.analysis.roomAnalysis.wallColor}</span></div>}
                    {vizResult.analysis.roomAnalysis.floorType && <div className="analysis-item"><span className="analysis-label">Flooring</span><span className="analysis-value">{vizResult.analysis.roomAnalysis.floorType}</span></div>}
                  </div>
                  {vizResult.analysis.keepItems && vizResult.analysis.keepItems.length > 0 && (
                    <div className="existing-furniture" style={{ marginTop: 12 }}>
                      <span className="analysis-label"><Check size={12} /> Keep These Items</span>
                      <div className="furniture-tags">{vizResult.analysis.keepItems.map((f: string, i: number) => <span key={i} className="furniture-tag keep-tag">{f}</span>)}</div>
                    </div>
                  )}
                  {vizResult.analysis.replaceItems && vizResult.analysis.replaceItems.length > 0 && (
                    <div className="existing-furniture" style={{ marginTop: 8 }}>
                      <span className="analysis-label"><ArrowRight size={12} /> Replace These Items</span>
                      <div className="furniture-tags">{vizResult.analysis.replaceItems.map((f: string, i: number) => <span key={i} className="furniture-tag replace-tag">{f}</span>)}</div>
                    </div>
                  )}
                  {vizResult.analysis.salesTips && vizResult.analysis.salesTips.length > 0 && (
                    <div className="sales-tips">
                      <span className="analysis-label"><Info size={12} /> Sales Tips</span>
                      <ul className="sales-tips-list">{vizResult.analysis.salesTips.map((tip: string, i: number) => <li key={i}>{tip}</li>)}</ul>
                    </div>
                  )}
                </div>
              )}

              {vizRoomPhoto && vizResult.analysis?.recommendedProducts && vizResult.analysis.recommendedProducts.length > 0 && (
                <div className="card" style={{ marginBottom: 20 }}>
                  <h3><Layers size={16} /> Room Concept Board</h3>
                  <p style={{ fontSize: 13, color: "var(--text-muted)", margin: "0 0 12px" }}>Ashley Furniture placement recommendations for your room</p>
                  <div className="concept-board-container">
                    <img src={vizRoomPhoto} alt="Your Room" className="concept-board-img" />
                    <div className="concept-board-overlay">
                      {vizResult.analysis.recommendedProducts.slice(0, 6).map((p: any, i: number) => {
                        const zone = p.placementZone;
                        if (!zone) return null;
                        const markerColors = ["#FF6B6B", "#4ECDC4", "#45B7D1", "#96CEB4", "#FFEAA7", "#DDA0DD"];
                        const color = markerColors[i % markerColors.length];
                        const getCatIcon = (cat: string) => {
                          switch (cat) {
                            case "Sofa": case "Sectional": case "Loveseat": return <Sofa size={16} />;
                            case "Recliner": case "Accent Chair": return <Armchair size={16} />;
                            case "Coffee Table": case "End Table": case "Dining Table": return <Coffee size={16} />;
                            case "Lamp": return <Lamp size={16} />;
                            case "TV Stand": return <Tv size={16} />;
                            case "Bed": return <BedDouble size={16} />;
                            case "Rug": return <Square size={16} />;
                            case "Dresser": case "Nightstand": case "Desk": return <Table2 size={16} />;
                            default: return <Armchair size={16} />;
                          }
                        };
                        return (
                          <div key={i} className="concept-marker-group" style={{ left: `${zone.x * 100}%`, top: `${zone.y * 100}%` }}>
                            <div className="concept-zone-outline" style={{
                              width: `${(zone.width || 0.2) * 100}%`,
                              height: `${(zone.height || 0.15) * 100}%`,
                              borderColor: color,
                              background: `${color}15`,
                            }} />
                            <div className="concept-marker-pin" style={{ background: color }}>
                              {getCatIcon(p.category || "")}
                            </div>
                            <div className="concept-marker-label" style={{ borderColor: color }}>
                              <span className="concept-marker-num" style={{ background: color }}>{i + 1}</span>
                              <span className="concept-marker-name">{p.name || p.collection || "Furniture"}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              {vizResult.analysis?.recommendedProducts && vizResult.analysis.recommendedProducts.length > 0 && (
                <div className="card" style={{ marginBottom: 20 }}>
                  <h3><ShoppingCart size={16} /> Recommended Ashley Furniture</h3>
                  <div className="products-grid">
                    {vizResult.analysis.recommendedProducts.slice(0, 6).map((p, i) => {
                      const searchTerm = p.searchSlug
                        ? p.searchSlug.replace(/-/g, " ")
                        : p.collection || p.name || "";
                      const ashleySearchUrl = `https://www.ashleyfurniture.com/search/?q=${encodeURIComponent(searchTerm)}`;
                      const getCategoryIcon = (cat: string) => {
                        switch (cat) {
                          case "Sofa": case "Sectional": case "Loveseat": return <Sofa size={28} />;
                          case "Recliner": case "Accent Chair": return <Armchair size={28} />;
                          case "Coffee Table": case "End Table": case "Dining Table": return <Coffee size={28} />;
                          case "Lamp": return <Lamp size={28} />;
                          case "TV Stand": return <Tv size={28} />;
                          case "Bed": return <BedDouble size={28} />;
                          case "Rug": return <Square size={28} />;
                          case "Dresser": case "Nightstand": case "Desk": return <Table2 size={28} />;
                          default: return <Armchair size={28} />;
                        }
                      };
                      const markerColors = ["#FF6B6B", "#4ECDC4", "#45B7D1", "#96CEB4", "#FFEAA7", "#DDA0DD"];
                      return (
                        <a key={i} href={ashleySearchUrl} target="_blank" rel="noopener noreferrer" className="product-rec-card product-rec-card-link">
                          <div className="product-rec-thumb">
                            {vizRoomPhoto && <span className="product-rec-num" style={{ background: markerColors[i % markerColors.length] }}>{i + 1}</span>}
                            <span className="product-rec-icon">{getCategoryIcon(p.category || "")}</span>
                            <span className="product-rec-cat-label">{p.category || "Furniture"}</span>
                          </div>
                          <div className="product-rec-info">
                            <span className="product-rec-name">{p.name}</span>
                            {p.collection && <span className="product-rec-collection">{p.collection} Collection</span>}
                            {p.replaces && p.replaces !== "New addition" && <span className="product-rec-replaces"><ArrowRight size={10} /> Replaces: {p.replaces}</span>}
                            {p.replaces === "New addition" && <span className="product-rec-replaces new-addition"><Sparkles size={10} /> New addition</span>}
                            <span className="product-rec-placement">{p.placement}</span>
                            {p.reason && <span className="product-rec-reason">{p.reason}</span>}
                            <span className="product-rec-link">View on Ashley Furniture <ArrowRight size={12} /></span>
                          </div>
                        </a>
                      );
                    })}
                  </div>
                </div>
              )}

              {vizResult.images && vizResult.images.filter((_: any, i: number) => !failedInspImgs.has(i)).length > 0 && (
                <div className="card" style={{ marginBottom: 20 }}>
                  <h3><Camera size={16} /> Room Inspiration</h3>
                  <p style={{ fontSize: 13, color: "var(--text-muted)", margin: "0 0 12px" }}>Styled rooms matching your preferences</p>
                  <div className="room-inspiration-grid">
                    {vizResult.images.map((img: any, i: number) => {
                      if (failedInspImgs.has(i)) return null;
                      return (
                        <div key={i} className="room-inspiration-card">
                          <img
                            src={img.url}
                            alt={`${img.style} ${img.furniture} ${img.room}`}
                            className="room-inspiration-img"
                            onError={() => setFailedInspImgs((prev) => new Set(prev).add(i))}
                          />
                          <div className="room-inspiration-meta">
                            <span className="room-insp-style">{img.style}</span>
                            <span className="room-insp-type">{img.furniture} - {img.room}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {vizResult.description && (
                <div className="card viz-description-card">
                  <h3><Palette size={16} /> AI Design Vision</h3>
                  <div className="viz-description-text">{vizResult.description}</div>
                </div>
              )}

              {vizRoomPhoto && vizResult.analysis && (
                <div className="card follow-up-card" style={{ marginTop: 20 }}>
                  <h3><MessageSquare size={16} /> Follow-Up Suggestions</h3>
                  <p className="follow-up-hint">Tell the AI to adjust the recommendations</p>
                  {vizConversationHistory.length > 0 && (
                    <div className="follow-up-history">
                      {vizConversationHistory.map((msg, i) => (
                        <div key={i} className={`follow-up-msg ${msg.role}`}>
                          <div className="follow-up-msg-icon">{msg.role === "user" ? <User size={14} /> : <Sparkles size={14} />}</div>
                          <div className="follow-up-msg-text">{msg.text}</div>
                        </div>
                      ))}
                      {isVizFollowingUp && (
                        <div className="follow-up-msg ai">
                          <div className="follow-up-msg-icon"><Sparkles size={14} /></div>
                          <div className="follow-up-msg-text"><Loader2 size={14} className="spinning" /> Updating...</div>
                        </div>
                      )}
                    </div>
                  )}
                  <div className="follow-up-input-row">
                    <input type="text" className="follow-up-input" placeholder="e.g., 'Change the sofa to a sectional'" value={vizFollowUpMessage}
                      onChange={(e) => setVizFollowUpMessage(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendVizFollowUp()} disabled={isVizFollowingUp} />
                    <button className="btn btn-primary" onClick={sendVizFollowUp} disabled={isVizFollowingUp || !vizFollowUpMessage.trim()}>
                      {isVizFollowingUp ? <Loader2 size={16} className="spinning" /> : <Send size={16} />}
                    </button>
                  </div>
                </div>
              )}

              <button className="btn btn-secondary" onClick={() => { setVizResult(null); setVizConversationHistory([]); setFailedInspImgs(new Set()); }} style={{ marginTop: 16 }}>Start Over</button>
            </div>
          )}
        </div>
      )}

      <canvas ref={vizCanvasRef} style={{ display: "none" }} />
    </div>
  );
}
